import i18next from 'i18next'
import { useActionStore } from '@/components/action-snackbar/action-snackbar-store'

// Failure surface of the catalogue queries (review of PR #219). `use-catalogs.ts` swallows
// `isError` on purpose — its eight consumers destructure the array and nothing else — so a failed
// catalogue used to die silently: empty dropdowns, a legend reduced to its header (the reveal
// hides a section whose catalogue never arrives), and no trail anywhere. Decision log §186 is the
// precedent: a mis-shared connection emptied the whole app for every non-owner and cost a live
// debugging session to locate. This is the ONE place every catalogue failure lands: the queries
// self-identify through `meta.catalogue` and the global QueryCache (provider.tsx) routes them
// here once React Query has exhausted its retries.

const warned = new Set<string>()

/**
 * Global QueryCache error hook. Only queries that opted in through `meta.catalogue` are acted on,
 * so the app's other queries keep their own error handling. It fires AFTER the retries are spent:
 * this is a final verdict, not a transient blip. The query is typed by the one field read from it
 * — `Query`'s generics are invariant across the cache's and the app's default error types, and a
 * structural parameter sidesteps that while accepting anything the cache hands over.
 */
export function onQueryCacheError(
  error: Error,
  query: { meta?: Record<string, unknown> },
): void {
  const catalogue = query.meta?.catalogue
  if (typeof catalogue !== 'string') return
  // One warning per catalogue and session: a shared root cause (connection not shared, expired
  // token) fails several catalogues at once and would stack identical snackbars otherwise.
  if (warned.has(catalogue)) return
  warned.add(catalogue)
  // The console line is the diagnosis trail the snackbar cannot carry: WHICH catalogue and why.
  // eslint-disable-next-line no-console
  console.error(`[catalogues] '${catalogue}' failed to load:`, error)
  useActionStore.getState().setAction({
    id: 'catalogue-error',
    severity: 'error',
    title: i18next.t(
      'catalogues.loadError',
      'Reference data failed to load. Reload the page to retry.',
    ),
  })
}

/** Test seam: the dedupe is module state, and each case needs a clean slate. */
export function resetCatalogueWarnings(): void {
  warned.clear()
}
